import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from '@radix-ui/react-icons';
import { Box, Card, Flex, Heading, IconButton } from '@radix-ui/themes';
import { AimingContextHelper } from 'classes/helpers/aiming-context.helper';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { StringHelper } from 'classes/helpers/string.helper';
import {
  DOT_RGB_BLUE,
  DOT_RGB_GREEN,
  DOT_RGB_YELLOW,
  PlateCanvas,
} from 'classes/plate-canvas';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import env from 'config';
import { AuthContext } from 'contexts/auth.context';
import { CookiesContext } from 'contexts/cookies.context';
import { MachineContext } from 'contexts/machine.context';
import { MatchingShotsContext } from 'contexts/pitch-lists/matching-shots.context';
import { isAfter, parseISO } from 'date-fns';
import {
  BASEBALL_PNG_PX,
  DOT_SIZE_SM,
  MAX_SHOT_OPACITY,
  SHOT_OPACITY_DELTA,
} from 'enums/canvas';
import useLatest from 'hooks/useLatestRef';
import { DefaultTFuncReturn, t } from 'i18next';
import { isAppearanceDark } from 'index';
import { MIN_SHOTS_TO_ROTATE } from 'lib_ts/classes/ball.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import { clamp, numberToImperial } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { PlateWarning } from 'lib_ts/enums/plate.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICovarianceSummary, IEllipse } from 'lib_ts/interfaces/i-ellipses';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import {
  DEFAULT_PLATE,
  IPitch,
  IPlateLoc,
  IPlateLocExt,
} from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import _ from 'lodash';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-input-slider';
import { Debugger } from './debugger';
import { ToggleShots } from './toggle-shots';

const COMPONENT_NAME = 'PlateView';

const REDRAW_DEQUEUE_MS = 100;

const ENABLE_SAFETY_NOTIFICATIONS = false;
const ENABLE_EXTRA_CLUSTER = false;
const ENABLE_REDRAW_WHILE_DRAGGING = false;

interface IReleaseAxis {
  axis: 'x' | 'y';
  upIcon: JSX.Element;
  downIcon: JSX.Element;
}

const CONTROLS: IReleaseAxis[] = [
  {
    axis: 'x',
    upIcon: <ArrowRightIcon />,
    downIcon: <ArrowLeftIcon />,
  },
  {
    axis: 'y',
    upIcon: <ArrowUpIcon />,
    downIcon: <ArrowDownIcon />,
  },
];

interface IProps extends Partial<IPlateLoc> {
  matchesChanged?: number;

  // should only be true if within a matching context
  matching?: boolean;

  // allows temporary suppression of new shots (e.g. sidebar is visible in the bg while training in a dialog)
  drawShots?: boolean;

  pitch?: IPitch;
  hitter?: IHitter;

  onUpdate: (location: IPlateLoc) => void;
  onMatchesChanged?: (newPitch: boolean) => void;

  border?: boolean;
  disabled?: boolean;

  showControls?: boolean;
  landscape?: boolean;
}

export const PlateView = (props: IProps) => {
  // for identifying new shots
  const [mountDate] = useState(new Date());

  const { current } = useContext(AuthContext);
  const { app } = useContext(CookiesContext);
  const { machine } = useContext(MachineContext);
  const { isPitchTrained, safeGetShotsByPitch } =
    useContext(MatchingShotsContext);

  const [canvasDef] = useState(
    props.landscape
      ? PlateCanvas.makeLandscape(isAppearanceDark())
      : PlateCanvas.makePortrait(isAppearanceDark())
  );
  const [testing, setTesting] = useState(false);
  const [bounds, setBounds] = useState(false);

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const ellipsesCanvas = useRef<HTMLCanvasElement>(null);
  const safetyCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();

  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // prevents the redraw logic while dragging
  const [dragging, setDragging] = useState(false);

  // when dragging finishes => queue up a redraw
  useEffect(() => {
    if (dragging) {
      return;
    }

    setLastUpdate(Date.now());
  }, [dragging]);

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    // ignore last updates while dragging
    if (!ENABLE_REDRAW_WHILE_DRAGGING && dragging) {
      return;
    }

    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        mainCanvas.current,
        ellipsesCanvas.current,
        safetyCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  const [slider, setSlider] = useState<IPlateLoc>({
    plate_x: props.plate_x ?? canvasDef.CONFIG.x.default_ft,
    plate_z: props.plate_z ?? canvasDef.CONFIG.y.default_ft,
  });

  const sliderRef = useLatest(slider);

  // plate changed, force slider to follow
  useEffect(() => {
    console.debug('Updating slider because plate changed');

    setSlider({
      plate_x: props.plate_x ?? canvasDef.CONFIG.x.default_ft,
      plate_z: props.plate_z ?? canvasDef.CONFIG.y.default_ft,
    });
  }, [props.plate_x, props.plate_z]);

  const [testSlider, setTestSlider] = useState<IPlateLoc>({
    plate_x: 0,
    plate_z: 0,
  });

  /** whenever a new shot comes in while the plate view is open */
  const [newShot, setNewShot] = useState<IMachineShot>();

  // drawMain
  useEffect(() => {
    try {
      const canvas = mainCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      console.debug('drawMain');

      canvasDef.drawStrikeZone(ctx);
      canvasDef.drawGround(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, []);

  const [covSummary, setCovarianceSummary] = useState<ICovarianceSummary>();

  const currEllipseRef = useLatest(covSummary?.ellipse);

  // backed up before changing covarianceSummary
  const [prevEllipse, setPreviousEllipse] = useState<IEllipse>();

  // drawEllipses
  useEffect(() => {
    try {
      const canvas = ellipsesCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.disabled) {
        return;
      }

      if (!current.plate_show_ellipses) {
        return;
      }

      const currEllipse = covSummary?.ellipse;

      if (!currEllipse) {
        return;
      }

      console.debug('drawEllipses');

      /** don't print the multi-color ellipses in production */
      const isDebugging = env.enable.toggle_plate_debug && app.plate_show_debug;

      const ellipses: {
        color: string;
        ellipse: IEllipse;
        fixFn: (n: number) => number;
      }[] = [];

      if (env.production || !isDebugging) {
        /** either prod OR not debugging */
        ellipses.push({
          /** white */
          color: '255, 255, 255',
          ellipse: currEllipse,
          fixFn: EllipseHelper.flipQ1AndQ4,
        });
      } else {
        /** must be non-prod AND debugging */
        ellipses.push({
          /** red */
          color: '255, 0, 0',
          ellipse: currEllipse,
          fixFn: EllipseHelper.flipQ1AndQ4,
        });

        if (testing) {
          ellipses.push({
            /** light blue - unrotated ellipse */
            color: '49, 210, 242',
            ellipse: currEllipse,
            fixFn: () => 0,
          });
        }
      }

      if (!props.pitch) {
        return;
      }

      if (!props.matching) {
        return;
      }

      const aimed = AimingContextHelper.getAdHocAimed({
        source: `${COMPONENT_NAME} > drawEllipses`,
        machine: machine,
        pitch: props.pitch,
        plate: slider,
        usingShots: safeGetShotsByPitch(props.pitch),
      });

      if (!aimed) {
        return;
      }

      const covResults = EllipseHelper.getCovarianceSummary({
        ms: aimed.ms,
        traj: aimed.pitch.traj,
        shots: safeGetShotsByPitch(aimed.pitch),
        plate_distance: machine.plate_distance,
      });

      ellipses.forEach((m) => {
        EllipseHelper.NON_EMPTY_SCALINGS.forEach((s) => {
          canvasDef.drawEllipse(ctx, {
            plate: covResults.mean_location,
            fillStyle: `rgba(${m.color},${s.opacity})`,
            ellipse: m.ellipse,
            scaling_factor: s.factor,
            angle_radians: m.fixFn(m.ellipse.angle_radians),
            bounds: bounds,
            drawBorder: s.drawBorder,
          });
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [
    app.plate_show_debug,
    current.plate_show_ellipses,
    machine.ball_type,
    machine.machineID,
    machine.plate_distance,
    props.disabled,
    props.matching,
    props.pitch,
    covSummary,
    slider,
  ]);

  const isDrawShots = useCallback(
    (mode: 'actual' | 'rotated' | 'any'): boolean => {
      if (!props.drawShots) {
        return false;
      }

      if (!props.matching) {
        return false;
      }

      if (!props.pitch) {
        return false;
      }

      if (!isPitchTrained(props.pitch)) {
        return false;
      }

      if (safeGetShotsByPitch(props.pitch).length === 0) {
        return false;
      }

      const needActual = app.plate_show_actual;
      const needRotated =
        app.plate_show_rotated ||
        [TrainingMode.Quick, TrainingMode.Manual].includes(
          current.training_mode ?? TrainingMode.Quick
        );

      switch (mode) {
        case 'any': {
          return needActual || needRotated || testing;
        }

        case 'actual': {
          return needActual;
        }

        case 'rotated': {
          return needRotated;
        }

        default: {
          return false;
        }
      }
    },
    [
      app.plate_show_actual,
      app.plate_show_rotated,
      current.training_mode,
      safeGetShotsByPitch,
      props.drawShots,
      props.matching,
      props.pitch,
      isPitchTrained,
    ]
  );

  // drawShots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!props.drawShots) {
          return;
        }

        if (props.disabled) {
          return;
        }

        console.debug('drawShots');

        const drawTestingDot = async () => {
          await canvasDef.drawDot(ctx, {
            plate: testSlider,
            color: `rgba(${DOT_RGB_BLUE},1)`,
            size: DOT_SIZE_SM,
            labelText: 'Test',
          });

          const currEllipse = covSummary?.ellipse;

          if (currEllipse) {
            /** draw location of un-rotated test dot */
            const testPlate = EllipseHelper.rotatePoint({
              origin: _.clone(slider),
              point: _.clone(testSlider),
              angle_radians: -currEllipse.angle_radians,
            });

            await canvasDef.drawDot(ctx, {
              plate: testPlate,
              color: `rgba(${DOT_RGB_BLUE},1)`,
              size: DOT_SIZE_SM,
              labelText: 'Test (Unrotated)',
            });
          }
        };

        const drawRawShots = async () => {
          if (!props.pitch) {
            return;
          }

          if (!props.matching) {
            return;
          }

          console.debug('drawRawShots');

          const shots = safeGetShotsByPitch(props.pitch);

          const plates = shots.map((shot) => {
            const traj = shot.user_traj ?? shot.traj;

            const transTraj = TrajHelper.translate(traj, {
              px: traj.px,
              pz: traj.pz,
              py: machine.plate_distance,
            });

            const o: IPlateLocExt = {
              ...TrajHelper.getPlateLoc(transTraj),
              _created: shot._created,
              _id: shot._id,
            };

            return o;
          });

          for (const p of plates) {
            const opacity = Math.max(
              0,
              MAX_SHOT_OPACITY -
                plates.findIndex((m) => m === p) * SHOT_OPACITY_DELTA
            );

            await canvasDef.drawDot(ctx, {
              plate: p,
              color: `rgba(${DOT_RGB_YELLOW}, ${opacity})`,
              size: DOT_SIZE_SM,
              labelText: testing ? p._created : undefined,
            });
          }
        };

        const drawRotatedShots = async () => {
          if (!props.pitch) {
            return;
          }

          if (!props.matching) {
            return;
          }

          console.debug('drawRotatedShots');

          const aimed = AimingContextHelper.getAdHocAimed({
            source: `${COMPONENT_NAME} > drawRotatedShots`,
            machine: machine,
            pitch: props.pitch,
            plate: { ...slider },
            usingShots: safeGetShotsByPitch(props.pitch),
          });

          if (!aimed) {
            return;
          }

          if (aimed.usingShots.length < MIN_SHOTS_TO_ROTATE) {
            return;
          }

          // excludes new shot
          const plates = EllipseHelper.getRotatedLocations({
            ms: aimed.ms,
            traj: aimed.pitch.traj,
            shots: aimed.usingShots,
            plate_distance: machine.plate_distance,
          }).filter((loc) => loc._id !== newShot?._id);

          for (const p of plates) {
            const opacity = Math.max(
              0,
              MAX_SHOT_OPACITY -
                plates.findIndex((m) => m === p) * SHOT_OPACITY_DELTA
            );

            await canvasDef.drawDot(ctx, {
              plate: p,
              color: `rgba(${DOT_RGB_GREEN}, ${opacity})`,
              size: DOT_SIZE_SM,
              labelText: testing ? p._created : undefined,
            });
          }
        };

        const drawNewShot = async () => {
          if (!newShot) {
            return;
          }

          console.debug('drawNewShot');

          const plate: IPlateLocExt = {
            ...TrajHelper.getPlateLoc(newShot.user_traj ?? newShot.traj),
            _created: newShot._created,
            _id: newShot._id,
          };

          await canvasDef.drawDot(ctx, {
            plate: plate,
            color: `rgba(${DOT_RGB_YELLOW}, ${MAX_SHOT_OPACITY})`,
            size: DOT_SIZE_SM,
            labelText: testing ? plate._created : undefined,
          });
        };

        if (testing) {
          await drawTestingDot();
        }

        if (isDrawShots('actual')) {
          await drawRawShots();
        }

        if (isDrawShots('rotated')) {
          await drawRotatedShots();
          await drawNewShot();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [
    props.disabled,
    props.drawShots,
    props.matching,
    props.pitch,
    safeGetShotsByPitch,
    isDrawShots,
    slider,
  ]);

  // drawSafetyRegion
  useEffect(() => {
    try {
      const canvas = safetyCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.disabled) {
        return;
      }

      if (!props.hitter) {
        return;
      }

      // todo: check if this is (now) drawing on the wrong side for some reason
      console.debug('drawSafetyRegion');
      canvasDef.drawStrikeZoneSafetyRegion(ctx, props.hitter);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [props.disabled, props.hitter]);

  // matches changed
  const matchesChanged = (isPitchNew: boolean) => {
    if (!props.pitch) {
      return;
    }

    if (!props.matching) {
      return;
    }

    console.debug(`matchesChanged (${isPitchNew ? 'new' : 'old'} pitch)`);

    const targetPlate = isPitchNew
      ? props.pitch.plate_loc_backup ?? DEFAULT_PLATE
      : slider;

    const shots = safeGetShotsByPitch(props.pitch);

    const aimed = AimingContextHelper.getAdHocAimed({
      source: `${COMPONENT_NAME} > matchesChanged`,
      machine: machine,
      pitch: props.pitch,
      plate: targetPlate,
      usingShots: shots,
    });

    if (!aimed) {
      console.debug({
        event: 'matchesChanged > failed to aim',
        targetPlate,
        shots,
      });
      return;
    }

    // backup the current value before computing the new one
    setPreviousEllipse(covSummary?.ellipse);

    const nextCovSummary = EllipseHelper.getCovarianceSummary({
      ms: aimed.ms,
      traj: aimed.pitch.traj,
      shots: aimed.usingShots,
      plate_distance: machine.plate_distance,
    });

    const { mean_location: nextPlate, ellipse } = nextCovSummary;

    if (isNaN(nextPlate.plate_x ?? NaN) || isNaN(nextPlate.plate_z ?? NaN)) {
      console.debug({
        event: 'matchesChanged > invalid covariance results (plate values)',
        cov: nextCovSummary,
      });
      return;
    }

    const newShot = aimed.usingShots
      .filter((s) => isAfter(parseISO(s._created), mountDate)) // skip shots from past sessions
      .sort((a, b) => -a._created.localeCompare(b._created))[0]; // desc _created > take the first if any

    if (ENABLE_SAFETY_NOTIFICATIONS && newShot && ellipse) {
      // creates session events / slack messages where necessary
      AimingContextHelper.detectSafetyEvent(newShot, ellipse, targetPlate);
    }

    setNewShot(newShot);

    // will cascade to activeEllipse
    setCovarianceSummary(nextCovSummary);

    if (isPitchNew) {
      setSlider(nextCovSummary.mean_location);
    }

    props.onMatchesChanged?.(isPitchNew);
  };

  // whenever pitch changes
  useEffect(() => matchesChanged(true), [props.pitch]);

  // whenever new shots arrived for the same pitch
  useEffect(() => matchesChanged(false), [props.matchesChanged]);

  useEffect(() => props.onUpdate(slider), [slider]);

  const [warningMsg, setWarningMsg] = useState<DefaultTFuncReturn>();

  const safetyCheck = () => {
    const ellipse = currEllipseRef.current;

    if (!ellipse) {
      // we cannot get a safe location without an ellipse
      return;
    }

    const safeLoc = EllipseHelper.getSafeOverallLoc({
      input: sliderRef.current,
      ellipseWarnings: app.plate_show_ellipse_warnings,
      ellipse: ellipse,
      hitter: props.hitter,
    });

    if (!safeLoc) {
      // there was an error, see console for details
      return;
    }

    switch (safeLoc.warning) {
      case PlateWarning.DangerousHorizontal: {
        setWarningMsg(t('common.plate-warning-dangerous-horizontal'));
        break;
      }

      case PlateWarning.EllipseBoth: {
        setWarningMsg(t('common.plate-warning-ellipse-both'));
        break;
      }

      case PlateWarning.EllipseLeft: {
        setWarningMsg(t('common.plate-warning-ellipse-left'));
        break;
      }

      case PlateWarning.EllipseRight: {
        setWarningMsg(t('common.plate-warning-ellipse-right'));
        break;
      }

      default: {
        setWarningMsg(undefined);
        break;
      }
    }

    // force the aim to be further from the side w/ the batter
    if (
      env.enable.toggle_plate_safety_controls &&
      app.plate_limit_by_ellipse &&
      safeLoc.location
    ) {
      // * currently shouldn't trigger because ellipse helper does not make recommendations
      console.debug({
        event: 'safetyCheck will adjust slider to safe location',
        inputLocation: sliderRef.current,
        safeLocation: safeLoc.location,
      });

      setSlider(safeLoc.location);
    }
  };

  const sliderContent = (
    <div className="slider" data-testid="PlateView">
      <div
        className="sizer"
        style={
          props.landscape
            ? {
                aspectRatio: '1.6',
                height: '300px',
              }
            : {
                width: '240px',
                aspectRatio: '0.77',
              }
        }
      >
        {/* for log rocket recordings to show the results */}
        {imgSrc && <img className="image-wrapper" src={imgSrc} />}
        <canvas
          ref={mergeCanvas}
          width={canvasDef.CONFIG.canvas.width_px}
          height={canvasDef.CONFIG.canvas.height_px}
        />
        <canvas
          ref={mainCanvas}
          width={canvasDef.CONFIG.canvas.width_px}
          height={canvasDef.CONFIG.canvas.height_px}
        />
        <canvas
          ref={ellipsesCanvas}
          width={canvasDef.CONFIG.canvas.width_px}
          height={canvasDef.CONFIG.canvas.height_px}
        />
        <canvas
          ref={safetyCanvas}
          width={canvasDef.CONFIG.canvas.width_px}
          height={canvasDef.CONFIG.canvas.height_px}
        />
        <canvas
          ref={shotsCanvas}
          width={canvasDef.CONFIG.canvas.width_px}
          height={canvasDef.CONFIG.canvas.height_px}
        />
        <div
          className={StringHelper.classNames([
            'slider-wrapper',
            isDrawShots('any') ? 'animate-fade' : '',
          ])}
        >
          <Slider
            data-testid="PlateViewSlider"
            data-xcord={slider.plate_x}
            data-ycord={slider.plate_z}
            axis="xy"
            xstep={canvasDef.CONFIG.x.step}
            xmin={canvasDef.CONFIG.x.min_ft}
            xmax={canvasDef.CONFIG.x.max_ft}
            x={slider.plate_x}
            ystep={canvasDef.CONFIG.y.step}
            ymin={canvasDef.CONFIG.y.min_ft}
            ymax={canvasDef.CONFIG.y.max_ft}
            y={slider.plate_z}
            onChange={(values) => {
              if (testing) {
                console.debug('setting test slider', values);
                setTestSlider({
                  plate_x: values.x,
                  plate_z: values.y,
                });
                return;
              }

              console.debug('setting slider', values);
              setSlider({
                plate_x: values.x,
                plate_z: values.y,
              });
            }}
            onDragStart={() => setDragging(true)}
            onDragEnd={() => {
              setDragging(false);

              // allow setSlider from onChange to settle the final value of slider
              setTimeout(() => {
                safetyCheck();
              }, 100);
            }}
            styles={{
              track: {
                backgroundColor: 'rgba(0, 0, 255, 0)',
                width: '100%',
                height: '100%',
              },
              thumb: {
                backgroundColor: 'rgba(0, 255, 0, 0)',
                backgroundImage: 'url(/img/baseball.png)',
                backgroundSize: 'cover',
                boxShadow: 'none',
                backgroundRepeat: 'no-repeat',
                height: BASEBALL_PNG_PX,
                width: BASEBALL_PNG_PX,
                margin: -BASEBALL_PNG_PX / 2,
                opacity: props.disabled ? 0 : 1,
              },
            }}
            yreverse
          />
        </div>
      </div>
    </div>
  );

  const summaries = useMemo<
    {
      header: string;
      content: string;
    }[]
  >(
    () => [
      {
        header: 'common.side',
        content: numberToImperial(slider.plate_x).display,
      },
      {
        header: 'common.height',
        content: numberToImperial(slider.plate_z).display,
      },
    ],
    [slider]
  );

  /**
   *
   * @param axis 'x' or 'y'
   * @param amount given in feet
   */
  const adjustPosition = (axis: string, amount: number) => {
    /** component is updating sliders, trigger parent's onUpdate with end results */
    const callback = (x: number, y: number) =>
      props.onUpdate({ plate_x: x, plate_z: y });

    switch (axis) {
      case 'x': {
        const nextX = clamp(
          numberToImperial(sliderRef.current.plate_x + amount).nearestInchFt,
          canvasDef.CONFIG.x.min_ft,
          canvasDef.CONFIG.x.max_ft
        );

        setSlider((prev) => ({
          ...prev,
          plate_x: nextX,
        }));

        callback(nextX, slider.plate_z);
        break;
      }

      case 'y': {
        const nextZ = clamp(
          numberToImperial(sliderRef.current.plate_z + amount).nearestInchFt,
          canvasDef.CONFIG.y.min_ft,
          canvasDef.CONFIG.y.max_ft
        );

        setSlider((prev) => ({
          ...prev,
          plate_z: nextZ,
        }));

        callback(slider.plate_x, nextZ);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        {props.border ? (
          <Card
            style={{
              padding: 0,
              background: 'var(--color-background)',
            }}
          >
            {sliderContent}
          </Card>
        ) : (
          sliderContent
        )}

        {props.showControls && (
          <Flex
            gap={RADIX.FLEX.GAP.SM}
            pl="6"
            pr="6"
            justify="center"
            align="center"
          >
            {summaries.map((item, i) => (
              <Card key={`summary-${i}`} style={{ minWidth: '100px' }}>
                <Flex direction="column" align="center">
                  <Heading size={RADIX.HEADING.SIZE.SM}>
                    {t(item.header)}
                  </Heading>

                  {item.content}
                </Flex>
              </Card>
            ))}
          </Flex>
        )}

        {props.showControls && (
          <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
            {CONTROLS.map((item, i) => (
              <Fragment key={`control-${i}`}>
                <Box>
                  <IconButton
                    color={RADIX.COLOR.NEUTRAL}
                    variant="soft"
                    onClick={() => adjustPosition(item.axis, -1 / 12)}
                  >
                    {item.downIcon}
                  </IconButton>
                </Box>
                <Box>
                  <IconButton
                    color={RADIX.COLOR.NEUTRAL}
                    variant="soft"
                    onClick={() => adjustPosition(item.axis, 1 / 12)}
                  >
                    {item.upIcon}
                  </IconButton>
                </Box>
              </Fragment>
            ))}
          </Flex>
        )}

        {warningMsg && (
          <CommonCallout
            color={
              app.plate_limit_by_ellipse
                ? RADIX.COLOR.INFO
                : RADIX.COLOR.WARNING
            }
            text_md={[
              warningMsg,
              t(
                app.plate_limit_by_ellipse
                  ? 'common.plate-adjustment-msg'
                  : 'common.proceed-with-caution'
              ),
            ].join('\n\n')}
          />
        )}

        {ENABLE_EXTRA_CLUSTER &&
          env.enable.toggle_plate_shots &&
          props.pitch &&
          props.matching && (
            <ToggleShots
              bounds={bounds}
              setBounds={setBounds}
              testing={testing}
              setTesting={setTesting}
            />
          )}

        {ENABLE_EXTRA_CLUSTER &&
          env.enable.toggle_plate_debug &&
          app.plate_show_debug && (
            <Debugger
              pitch={props.pitch}
              newShot={newShot}
              matching={props.matching}
              testing={testing}
              slider={slider}
              testSlider={testSlider}
              currEllipse={covSummary?.ellipse}
              prevEllipse={prevEllipse}
              covSummary={covSummary}
            />
          )}
      </Flex>
    </ErrorBoundary>
  );
};
